import React, { useRef, useState, useMemo } from 'react';
import Table from 'react-bootstrap/Table';
import { BiDisc } from 'react-icons/bi';
import { FiCalendar, FiTag, FiUser } from 'react-icons/fi';
import Styles from './KanbanList.module.scss';
import TableHead from 'Components/TableHeader/TableHead';
import ContentPlannerTable from 'Components/ContentPlanner/ContentPlannerTable';
import TaskGroup from 'Components/ContentPlanner/TaskGroup';
import { groupTasksByViewMode, getViewModeConfig } from '../../config/groupingConfig';

const TableHeadings = [
  {
    id: 'deadline',
    title: 'Deadline',
    icon: <FiCalendar color="#667085" size={16} />,
  },
  {
    id: 'assignedTo',
    title: 'Assigned to',
    icon: <FiUser color="#667085" size={16} />,
  },
  {
    id: 'status',
    title: 'Status',
    icon: <BiDisc color="#667085" size={16} />,
  },
  {
    id: 'contentType',
    title: 'Content Type',
    icon: <FiTag color="#667085" size={16} />,
  },
];

function KanbanList({
  tasks,
  onDelete,
  onEdit,
  onMove,
  fetchCPTasks,
  onArchive,
  onUpdateBrief = () => {},
  selectedColumns = [
    ['deadline', true],
    ['assignedTo', true],
    ['status', true],
    ['contentType', true],
  ],
  updateColumns = null,
  calendarTable,
  handleAICreate,
  showCheckbox = false,
  selectedTaskIds = [],
  onTaskSelect,
  onSelectAll,
  isAllSelected = false,
  isIndeterminate = false,
  viewMode = 'general', // New prop for view mode
  projects = [], // Projects data for campaign view grouping
  brands = [], // Brands data for brand view grouping
}) {
  // State for tracking current sort
  const [currentSort, setCurrentSort] = useState(null);

  // Create a ref object (we don't need useRef hook for this simple use case)
  const sortRef = { current: {} };

  // Handle sort action from TableHead
  const handleSort = (column, direction) => {
    setCurrentSort({ column, direction });

    // Trigger sorting in the ContentPlannerTable via ref
    if (sortRef.current && sortRef.current.sortBy) {
      sortRef.current.sortBy(column, direction);
    }
  };

  // Group tasks by the selected view mode
  const groupedTasks = useMemo(() => {
    // Don't group for 'general' view or if no viewMode specified
    if (!viewMode || viewMode === 'general') return null;

    // Prepare lookup data for grouping
    const lookupData = {
      project_id: projects, // For campaign view
      brand_id: brands, // For brand view
    };

    return groupTasksByViewMode(tasks, viewMode, lookupData);
  }, [tasks, viewMode, projects, brands]);

  // Get view mode config for default expanded groups
  const viewConfig = useMemo(() => {
    return getViewModeConfig(viewMode);
  }, [viewMode]);

  // Render grouped accordion view (only for non-general views)
  if (groupedTasks && groupedTasks.length > 0 && viewMode !== 'general') {
    return (
      <section className={Styles.calendar_table}>
        {groupedTasks.map((group) => (
          <TaskGroup
            key={group.key}
            group={group}
            onDelete={onDelete}
            onMove={onMove}
            onEdit={onEdit}
            fetchCPTasks={fetchCPTasks}
            onArchive={onArchive}
            onUpdateBrief={onUpdateBrief}
            selectedColumns={selectedColumns}
            calendarTable={calendarTable}
            handleAICreate={handleAICreate}
            defaultExpanded={viewConfig.defaultExpanded?.includes(group.key)}
            showCheckbox={showCheckbox}
            selectedTaskIds={selectedTaskIds}
            onTaskSelect={onTaskSelect}
            sortRef={sortRef}
            initialSortColumn={currentSort?.column}
            initialSortDirection={currentSort?.direction}
            // TableHead props
            updateColumns={updateColumns}
            TableHeadings={TableHeadings}
            onSort={handleSort}
            currentSort={currentSort}
            onSelectAll={onSelectAll}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            viewMode={viewMode}
          />
        ))}
      </section>
    );
  }

  // Render regular flat table view (fallback)
  return (
    <section className={Styles.calendar_table}>
      <Table className="table-auto table-sticky" hover responsive="lg">
        <TableHead
          selectedColumns={selectedColumns}
          updateColumns={updateColumns}
          TableHeadings={TableHeadings}
          onSort={handleSort}
          currentSort={currentSort}
          showCheckbox={showCheckbox}
          onSelectAll={onSelectAll}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
        />
        <ContentPlannerTable
          tasks={tasks}
          onDelete={onDelete}
          onMove={onMove}
          onEdit={onEdit}
          fetchCPTasks={fetchCPTasks}
          onArchive={onArchive}
          onUpdateBrief={onUpdateBrief}
          selectedColumns={selectedColumns}
          calendarTable={calendarTable}
          handleAICreate={handleAICreate}
          sortRef={sortRef}
          initialSortColumn={currentSort?.column}
          initialSortDirection={currentSort?.direction}
          showCheckbox={showCheckbox}
          selectedTaskIds={selectedTaskIds}
          onTaskSelect={onTaskSelect}
        />
      </Table>
    </section>
  );
}

// Memoize KanbanList to prevent unnecessary re-renders
// Only re-render if key props change
export default React.memo(KanbanList, (prevProps, nextProps) => {
  return (
    prevProps.tasks.length === nextProps.tasks.length &&
    prevProps.selectedTaskIds.length === nextProps.selectedTaskIds.length &&
    prevProps.isAllSelected === nextProps.isAllSelected &&
    prevProps.isIndeterminate === nextProps.isIndeterminate &&
    prevProps.selectedColumns === nextProps.selectedColumns &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.showCheckbox === nextProps.showCheckbox
  );
});
